import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'dismissed_alerts';

const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'error':
      return <ErrorIcon fontSize="small" />;
    case 'warning':
      return <WarningIcon fontSize="small" />;
    case 'info':
    default:
      return <InfoIcon fontSize="small" />;
  }
};

const AlertsPanel = ({ alerts = [], loading = false, onAlertDismissed, showWhenEmpty = false, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  useEffect(() => {
    // Load dismissed alerts from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const dismissed = JSON.parse(stored);
        setDismissedAlerts(new Set(dismissed));
      } catch (e) {
        console.error('Error loading dismissed alerts:', e);
      }
    }
  }, []);

  const handleDismiss = (alertId) => {
    const newDismissed = new Set(dismissedAlerts);
    newDismissed.add(alertId);
    setDismissedAlerts(newDismissed);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(newDismissed)));
    if (onAlertDismissed) {
      onAlertDismissed(newDismissed);
    }
  };

  const handleAlertClick = (alertId, actionUrl, event) => {
    // Prevent navigation if clicking the dismiss button
    if (event?.target?.closest('button')) {
      return;
    }
    
    // Dismiss the alert
    handleDismiss(alertId);
    
    // Close the panel if callback provided
    if (onClose) {
      onClose();
    }
    
    // Navigate if there's an action URL
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const visibleAlerts = alerts.filter((alert) => !dismissedAlerts.has(alert.id));

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading alerts...
        </Typography>
      </Box>
    );
  }

  if (visibleAlerts.length === 0 && !showWhenEmpty) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <NotificationsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ flex: 1, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
          Alerts & Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {visibleAlerts.length}
        </Typography>
      </Box>

      {visibleAlerts.length > 0 && <Divider sx={{ mb: 2 }} />}

      {visibleAlerts.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visibleAlerts.map((alert) => (
            <Alert
              key={alert.id}
              severity={alert.severity}
              icon={getSeverityIcon(alert.severity)}
              action={
                <IconButton
                  aria-label="dismiss"
                  color="inherit"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(alert.id);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              onClick={(e) => handleAlertClick(alert.id, alert.actionUrl, e)}
              sx={{
                cursor: alert.actionUrl ? 'pointer' : 'default',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                '&:hover': alert.actionUrl
                  ? {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                    }
                  : {},
                transition: 'background-color 0.2s',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {alert.message}
              </Typography>
            </Alert>
          ))}
        </Box>
      ) : showWhenEmpty ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No alerts at this time
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default AlertsPanel;

